import { useMutation, UseMutationOptions } from "react-query";
import * as Redux from "react-redux";
import { Dispatch } from "redux";

import { logAmplitudeEvent } from "client/lib/amplitude";
import { FormService, UserService } from "client/services";
import {
  errorActions,
  FormCompletion,
  formCompletionActions,
  RootState,
  transformServerValidationErrors,
  userActions,
} from "client/store";
import { UserStateResponse } from "client/types";
import { allBusinessDetailsFormsComplete } from "client/utils";

type RequestArgs = {
  formCompletionData: FormCompletion;
  saveData: any;
  storeData: UserService.StoreData;
};

const updateStore = async (
  storeData: RequestArgs["storeData"]
): Promise<UserStateResponse> => {
  const updateStoreRes = await UserService.updateStoreInfo(storeData);
  if (!updateStoreRes?.validation_errors) {
    // adding 1 sec delay to ensure "submitted_store_form" fires after "submitted_business_owners_form" when final business owners form is submitted for data clarity
    setTimeout(
      () =>
        logAmplitudeEvent("REDESIGN_ADD_YOUR_STORE", "submitted_store_form"),
      1000
    );
  }
  return updateStoreRes;
};

const saveFormState = async (
  updateStoreRes: Partial<UserStateResponse>,
  formCompletionData: FormCompletion
): Promise<Partial<UserStateResponse>> => {
  const res = await FormService.saveFormState(formCompletionData);
  return { ...updateStoreRes, ...res };
};

const submitBusinessDetailsForm = async ({
  saveData,
  formCompletionData,
  storeData,
}: RequestArgs): Promise<Partial<UserStateResponse>> => {
  await UserService.saveMerchant(saveData);

  const updateStoreRes = allBusinessDetailsFormsComplete(formCompletionData)
    ? await updateStore(storeData)
    : ({} as Partial<UserStateResponse>);

  if (
    updateStoreRes.userState === "SQUARESPACE" ||
    updateStoreRes.userState === "SQUARE_ONLY" ||
    updateStoreRes.userState === "SQUARE_AND_MOP"
  ) {
    return updateStoreRes;
  }

  const validationErrorsResponse = updateStoreRes?.validation_errors
    ? updateStoreRes
    : null;

  if (validationErrorsResponse) {
    return validationErrorsResponse;
  }

  if (
    updateStoreRes?.formCompletedState &&
    allBusinessDetailsFormsComplete(updateStoreRes?.formCompletedState)
  ) {
    // on final form submit we skip hitting /update-form-completion
    return updateStoreRes;
  } else {
    // otherwise, we need to save the form state
    return saveFormState(updateStoreRes, formCompletionData);
  }
};

type FormDescription =
  | "Company and business type"
  | "About your store"
  | "Owner details";

const getError = (fields: string, form: FormDescription, errorId: string) =>
  `There was an issue with ${fields} - please reopen the ${form} form and try again. (errorId=${errorId})`;

export const getUserStateMutationOptions = (
  dispatch: Dispatch
): UseMutationOptions<Partial<UserStateResponse>, Error, any, any> => ({
  onSuccess: (
    { formCompletedState, userState, validation_errors }: any,
    vars: any
  ) => {
    if (validation_errors) {
      const transformedErrors = transformServerValidationErrors(
        validation_errors
      );
      const {
        companyInformation = {},
        owners = [],
        storeDetails = {},
      } = transformedErrors;

      /**
       * Determine if an error exists on any of our business details forms
       * If they exist, we'll display an error and set state so the header icon
       * changes to a red warning
       */
      const companyInfoErrors = Object.keys(companyInformation);
      const storeDetailsErrors = Object.keys(storeDetails);
      const ownerErrors = owners.filter((m) => Object.keys(m).length);

      // The Error Toast does not support stacked notifications - following the materialUI standard
      // The vast majority of form errors occur either in company info or business owner
      if (companyInfoErrors.length) {
        dispatch(
          errorActions.setError(
            getError(
              "a company information field",
              "Company and business type",
              validation_errors.errorId
            )
          )
        );
      } else if (storeDetailsErrors.length) {
        dispatch(
          errorActions.setError(
            getError(
              "a store details field",
              "About your store",
              validation_errors.errorId
            )
          )
        );
      } else if (ownerErrors.length) {
        dispatch(
          errorActions.setError(
            getError(
              "an owner field",
              "Owner details",
              validation_errors.errorId
            )
          )
        );
      }
      dispatch(errorActions.setFieldErrorMap(transformedErrors));
    } else {
      if (userState) {
        dispatch(userActions.setUserState(userState));
      }
      if (userState === "MAINTENANCE_MODE") {
        // we don't get form completion values if we're in maintenance mode
        return;
      }

      if (formCompletedState) {
        dispatch(formCompletionActions.setFormCompletion(formCompletedState));
        // log each successful form submission for each section (Company Information, Store Details, Business Owner) of the parent BusinessDetailsForm
        // additional logic checks to ensure duplicate logs are not firing every time useSaveFormCompletion mutation fn call fires
        if (
          formCompletedState.companyInfoForm &&
          !formCompletedState.storeDetailsForm &&
          !formCompletedState.businessOwnerForm
        ) {
          logAmplitudeEvent(
            "REDESIGN_ADD_YOUR_STORE",
            "submitted_company_info_form"
          );
        }
        if (
          formCompletedState.companyInfoForm &&
          formCompletedState.storeDetailsForm &&
          !formCompletedState.businessOwnerForm
        ) {
          logAmplitudeEvent(
            "REDESIGN_ADD_YOUR_STORE",
            "submitted_store_details_form"
          );
        }
        if (
          formCompletedState.companyInfoForm &&
          formCompletedState.storeDetailsForm &&
          formCompletedState.businessOwnerForm
        ) {
          logAmplitudeEvent(
            "REDESIGN_ADD_YOUR_STORE",
            "submitted_business_owners_form"
          );
        }
      }

      // selfServeInStore is a computed property that is calculated when StoreDetails form is submitted
      // logging when selfServeInStore is set to true
      if (vars?.storeData?.data?.selfServeInStore) {
        logAmplitudeEvent(
          "SELF_SERVE_INSTORE",
          "set_self_serve_instore_eligible"
        );
      }
    }
  },
  onError: (err: Error) => {
    dispatch(errorActions.setError(err.message));
  },
});

export function useSaveFormCompletion(dispatch: Dispatch): any {
  const formCompletionState = Redux.useSelector(
    ({ formCompletion }: RootState) => formCompletion
  );
  const options = getUserStateMutationOptions(dispatch);
  return useMutation(
    (args: RequestArgs) =>
      submitBusinessDetailsForm({
        ...args,
        formCompletionData: {
          ...formCompletionState,
          ...args.formCompletionData,
        },
      }),
    options
  );
}
